import { Icon } from '@iconify/react'
import { Spin, Tag } from 'antd'
import TextArea, { type TextAreaRef } from 'antd/es/input/TextArea'
import { useNotification } from 'app/providers'
import classNames from 'classnames'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { DateService } from 'common/services/dateService'
import { stringifyResponseForError } from 'common/utils/text.utils'
import {
  useFetchSoapNotesQuery,
  useSaveSoapNoteMutation,
} from 'features/Procedure/state/procedure.api'
import type { NoteKeyValue, TelehealthSoapNote } from 'features/Procedure/state/procedure-api.type'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  insertTextAtCursor,
  normalizeKeyValueEntries,
  resolveSectionSeed,
} from './notes.objective.utils'
import { ProcedureImageList } from './components/procedure-image-list'
import styles from './notes.module.scss'

type Note = {
  subjective: string | null
  objective: string | null
  assessment: string | null
  labels: Record<keyof NoteFormValues, NoteKeyValue[]>
  templates: Record<keyof NoteFormValues, NoteKeyValue[]>
}

type NoteFormValues = {
  subjective: string
  objective: string
  assessment: string
}

type DirtyFields = Record<keyof NoteFormValues, boolean>

type SectionSaveStatus = 'idle' | 'saving' | 'saved' | 'error'
type SectionSaveStatuses = Record<keyof NoteFormValues, SectionSaveStatus>
type SectionLastSavedAt = Record<keyof NoteFormValues, string | null>

type NoteDraftState = {
  values: NoteFormValues
  dirtyFields: DirtyFields
  lastSavedValues: NoteFormValues
}

type ActiveNoteContext = {
  patientPid: number
  encounterUid: string
  encounterKey: string
}

type PendingDraftSaveSnapshot = {
  context: ActiveNoteContext
  draft: NoteDraftState
}

export const NOTES_AUTOSAVE_DELAY_MS = 1000
export const NOTES_SAVE_RETRY_DELAY_MS = 5000
const NOTES_SAVE_MAX_RETRIES = 1
const NOTES_FLUSH_MAX_WAITS = 3

const emptyFormValues: NoteFormValues = {
  subjective: '',
  objective: '',
  assessment: '',
}

const sectionKeys: (keyof NoteFormValues)[] = ['subjective', 'objective', 'assessment']
const sectionLabels: Record<keyof NoteFormValues, string> = {
  subjective: 'Patient History',
  objective: 'Physical Exam',
  assessment: 'Assessment / Plan',
}

type TelehealthField = {
  key: keyof Omit<TelehealthSoapNote, 'id'>
  label: string
}

const telehealthFieldOrder: TelehealthField[] = [
  { key: 'subjective', label: 'Subjective' },
  { key: 'objective', label: 'Objective' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'plan', label: 'Plan' },
  { key: 'doctor_notes', label: 'Doctor Notes' },
]

const createEmptyDirtyFields = (): DirtyFields => ({
  subjective: false,
  objective: false,
  assessment: false,
})

const createEmptySectionSaveStatuses = (): SectionSaveStatuses => ({
  subjective: 'idle',
  objective: 'idle',
  assessment: 'idle',
})

const createEmptySectionLastSavedAt = (): SectionLastSavedAt => ({
  subjective: null,
  objective: null,
  assessment: null,
})

const createEmptyDraftState = (): NoteDraftState => ({
  values: { ...emptyFormValues },
  dirtyFields: createEmptyDirtyFields(),
  lastSavedValues: { ...emptyFormValues },
})

const cloneDraftState = (draft: NoteDraftState): NoteDraftState => ({
  values: { ...draft.values },
  dirtyFields: { ...draft.dirtyFields },
  lastSavedValues: { ...draft.lastSavedValues },
})

const normalizeFieldValueForSave = (value: string): string =>
  value.trim().length === 0 ? '' : value

const isFieldValueSavable = (value: string): boolean => {
  const normalizedValue = normalizeFieldValueForSave(value)
  return normalizedValue.length === 0 || normalizedValue.length >= 2
}

const getSavableDirtyFields = (draft: NoteDraftState): (keyof NoteFormValues)[] =>
  sectionKeys.filter(
    (field) => draft.dirtyFields[field] && isFieldValueSavable(draft.values[field]),
  )

const getDraftPayload = (
  draft: NoteDraftState,
  fields: (keyof NoteFormValues)[],
): Partial<NoteFormValues> => {
  const payload: Partial<NoteFormValues> = {}

  fields.forEach((field) => {
    payload[field] = normalizeFieldValueForSave(draft.values[field])
  })

  return payload
}

// Marks fields saved only if they were not edited again while the request was in flight.
const reconcileSavedDraft = (
  latestDraft: NoteDraftState,
  draftAtStart: NoteDraftState,
  payload: Partial<NoteFormValues>,
  fields: (keyof NoteFormValues)[],
): { draft: NoteDraftState; committedFields: (keyof NoteFormValues)[] } => {
  const committedFields = fields.filter(
    (field) => latestDraft.values[field] === draftAtStart.values[field],
  )
  const committedValues: Partial<NoteFormValues> = Object.fromEntries(
    committedFields.map((field) => [field, payload[field] ?? latestDraft.values[field]]),
  )
  const committedDirtyFields: Partial<DirtyFields> = Object.fromEntries(
    committedFields.map((field) => [field, false]),
  )

  return {
    committedFields,
    draft: {
      values: { ...latestDraft.values, ...committedValues },
      dirtyFields: { ...latestDraft.dirtyFields, ...committedDirtyFields },
      lastSavedValues: { ...latestDraft.lastSavedValues, ...committedValues },
    },
  }
}

const getAutosaveErrorDescription = (error: unknown): string => {
  const fallbackMessage = 'Unable to autosave note. Your draft is still preserved.'
  const errorRecord = (error && typeof error === 'object' ? error : null) as {
    message?: unknown
    data?: unknown
  } | null
  const dataRecord = (
    errorRecord?.data && typeof errorRecord.data === 'object' ? errorRecord.data : null
  ) as {
    message?: unknown
  } | null

  if (typeof dataRecord?.message === 'string' && dataRecord.message.length > 0) {
    return dataRecord.message
  }

  if (typeof errorRecord?.message === 'string' && errorRecord.message.length > 0) {
    return errorRecord.message
  }

  return `${fallbackMessage} Response: ${stringifyResponseForError(errorRecord?.data ?? error)}`
}

const toActiveNoteContext = (
  selectedPatientId: number | null,
  selectedEncounterUid: string | null,
): ActiveNoteContext | null => {
  if (!selectedPatientId || !selectedEncounterUid) {
    return null
  }

  return {
    patientPid: selectedPatientId,
    encounterUid: selectedEncounterUid,
    encounterKey: `${selectedPatientId}:${selectedEncounterUid}`,
  }
}

export type NotesProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
  // Controls the procedure photo list independently of the SOAP text sections.
  // When omitted it falls back to `disableActions`; callers pass it so photos stay
  // uploadable after the note is signed while the text remains read-only.
  disablePhotoActions?: boolean
  // Until the backend reports the SOAP form as signable, autosaves refresh the eSign status.
  hasESignFormToSign?: boolean
  // Renders a read-only telehealth SOAP notes section when the encounter has one.
  showTelehealthSoapNotes?: boolean
  className?: string
  defaultExpanded?: boolean
  onRegisterLeaveAction?: ((handler: (() => Promise<void>) | null) => void) | undefined
}

export const Notes = ({
  selectedPatientId,
  selectedEncounterUid,
  disableActions = false,
  disablePhotoActions,
  hasESignFormToSign = false,
  showTelehealthSoapNotes = false,
  className,
  defaultExpanded = true,
  onRegisterLeaveAction,
}: NotesProps) => {
  const { setNotification } = useNotification()

  const activeContext = useMemo(
    () => toActiveNoteContext(selectedPatientId, selectedEncounterUid),
    [selectedPatientId, selectedEncounterUid],
  )
  const activeEncounterKey = activeContext?.encounterKey ?? null
  const canMutate = Boolean(activeContext && !disableActions)
  const arePhotoActionsDisabled = disablePhotoActions ?? disableActions
  const shouldSkipSoapQuery = !activeContext

  const {
    currentData: soapNotesData,
    isLoading: isLoadingSoapNotes,
    isFetching: isFetchingSoapNotes,
    isUninitialized: isSoapNotesUninitialized,
  } = useFetchSoapNotesQuery(
    {
      patientPid: selectedPatientId ?? 0,
      encounterUid: selectedEncounterUid ?? '',
    },
    {
      skip: shouldSkipSoapQuery,
    },
  )

  const [saveSoapNote] = useSaveSoapNoteMutation()

  const notes = useMemo<Note[]>(() => {
    if (!soapNotesData) return []

    return soapNotesData.map((note) => ({
      subjective: note.subjective ?? null,
      objective: note.objective ?? null,
      assessment: note.assessment ?? null,
      labels: {
        subjective: normalizeKeyValueEntries(note.subjective_labels),
        objective: normalizeKeyValueEntries(note.objective_labels),
        assessment: normalizeKeyValueEntries(note.plan_labels),
      },
      templates: {
        subjective: normalizeKeyValueEntries(note.subjective_templates),
        objective: normalizeKeyValueEntries(note.objective_templates),
        assessment: normalizeKeyValueEntries(note.plan_templates),
      },
    }))
  }, [soapNotesData])

  const singleNote = notes[0] ?? null
  const labelsBySection = useMemo<Record<keyof NoteFormValues, NoteKeyValue[]>>(
    () => ({
      subjective: singleNote?.labels.subjective ?? [],
      objective: singleNote?.labels.objective ?? [],
      assessment: singleNote?.labels.assessment ?? [],
    }),
    [singleNote],
  )
  const seedBySection = useMemo<NoteFormValues>(
    () => ({
      subjective: resolveSectionSeed(
        singleNote?.subjective ?? null,
        singleNote?.templates.subjective ?? [],
      ),
      objective: resolveSectionSeed(
        singleNote?.objective ?? null,
        singleNote?.templates.objective ?? [],
      ),
      assessment: resolveSectionSeed(
        singleNote?.assessment ?? null,
        singleNote?.templates.assessment ?? [],
      ),
    }),
    [singleNote],
  )
  const telehealthFields = useMemo(() => {
    if (!showTelehealthSoapNotes) return []

    const telehealthNote = soapNotesData?.find(
      (note) => note.telehealth_soap_notes,
    )?.telehealth_soap_notes

    if (!telehealthNote) return []

    return telehealthFieldOrder
      .map(({ key, label }) => ({ key, label, value: (telehealthNote[key] ?? '').trim() }))
      .filter((field) => field.value.length > 0)
  }, [showTelehealthSoapNotes, soapNotesData])

  const isSoapContextResolving =
    !shouldSkipSoapQuery &&
    !soapNotesData &&
    (isLoadingSoapNotes || isFetchingSoapNotes || isSoapNotesUninitialized)

  const [expandedSections, setExpandedSections] = useState<Set<keyof NoteFormValues>>(() =>
    defaultExpanded ? new Set(sectionKeys) : new Set(),
  )
  const [isTelehealthExpanded, setIsTelehealthExpanded] = useState(defaultExpanded)
  const [draftState, setDraftState] = useState<NoteDraftState>(() => createEmptyDraftState())
  const [sectionSaveStatuses, setSectionSaveStatuses] = useState<SectionSaveStatuses>(() =>
    createEmptySectionSaveStatuses(),
  )
  const [sectionLastSavedAt, setSectionLastSavedAt] = useState<SectionLastSavedAt>(() =>
    createEmptySectionLastSavedAt(),
  )

  const previousEncounterKeyRef = useRef<string | null>(activeEncounterKey)
  const initializedEncounterKeyRef = useRef<string | null>(null)
  const encounterKeyRef = useRef<string | null>(activeEncounterKey)
  const activeContextRef = useRef<ActiveNoteContext | null>(activeContext)
  const draftStateRef = useRef<NoteDraftState>(draftState)
  const hasESignFormToSignRef = useRef(hasESignFormToSign)
  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textAreaRefs = useRef<Partial<Record<keyof NoteFormValues, TextAreaRef | null>>>({})
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null)
  const inFlightSaveRef = useRef<Promise<void> | null>(null)
  // Timers call the latest save without re-arming when its identity changes.
  const persistDraftRef = useRef<(snapshot: PendingDraftSaveSnapshot) => Promise<void>>(() =>
    Promise.resolve(),
  )
  const retryCountRef = useRef(0)
  const hasSoapNoteRef = useRef(Boolean(singleNote))
  const isMountedRef = useRef(true)

  useEffect(() => {
    encounterKeyRef.current = activeEncounterKey
    activeContextRef.current = activeContext
  }, [activeContext, activeEncounterKey])

  useEffect(() => {
    draftStateRef.current = draftState
  }, [draftState])

  useEffect(() => {
    hasESignFormToSignRef.current = hasESignFormToSign
  }, [hasESignFormToSign])

  useEffect(() => {
    hasSoapNoteRef.current = Boolean(singleNote)
  }, [singleNote])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
      autosaveTimeoutRef.current = null
    }
  }, [])

  const markSectionsStatus = useCallback(
    (fields: (keyof NoteFormValues)[], status: SectionSaveStatus) => {
      if (fields.length === 0) {
        return
      }

      setSectionSaveStatuses((prevStatuses) => {
        const nextStatuses = { ...prevStatuses }
        fields.forEach((field) => {
          nextStatuses[field] = status
        })
        return nextStatuses
      })
    },
    [],
  )

  const markSectionsSaved = useCallback(
    (fields: (keyof NoteFormValues)[]) => {
      if (fields.length === 0) {
        return
      }

      markSectionsStatus(fields, 'saved')
      const formattedTimestamp = DateService.formatDisplayDate(
        new Date().toISOString(),
        true,
        false,
        false,
      ).trim()

      setSectionLastSavedAt((prevTimestamps) => {
        const nextTimestamps = { ...prevTimestamps }
        fields.forEach((field) => {
          nextTimestamps[field] = formattedTimestamp
        })
        return nextTimestamps
      })
    },
    [markSectionsStatus],
  )

  const createPendingDraftSaveSnapshot = useCallback(
    (context: ActiveNoteContext | null): PendingDraftSaveSnapshot | null => {
      if (!context) {
        return null
      }

      return {
        context,
        draft: cloneDraftState(draftStateRef.current),
      }
    },
    [],
  )

  const persistDraftForContext = useCallback(
    (snapshot: PendingDraftSaveSnapshot): Promise<void> => {
      const { context, draft: draftAtStart } = snapshot

      if (disableActions || encounterKeyRef.current !== context.encounterKey) {
        return Promise.resolve()
      }

      if (inFlightSaveRef.current) {
        return inFlightSaveRef.current
      }

      const savableFieldsAtStart = getSavableDirtyFields(draftAtStart)
      if (savableFieldsAtStart.length === 0) {
        return Promise.resolve()
      }

      const isContextActive = () =>
        isMountedRef.current && encounterKeyRef.current === context.encounterKey

      markSectionsStatus(savableFieldsAtStart, 'saving')

      let isSettled = false
      const saveTask = (async () => {
        try {
          const payload = getDraftPayload(draftAtStart, savableFieldsAtStart)

          await saveSoapNote({
            patientPid: context.patientPid,
            encounterUid: context.encounterUid,
            payload,
            skipESignInvalidation: hasESignFormToSignRef.current,
            isFirstSave: !hasSoapNoteRef.current,
          }).unwrap()

          retryCountRef.current = 0
          if (!isContextActive()) {
            return
          }

          const { draft: nextDraft, committedFields } = reconcileSavedDraft(
            draftStateRef.current,
            draftAtStart,
            payload,
            savableFieldsAtStart,
          )
          draftStateRef.current = nextDraft
          setDraftState(nextDraft)
          markSectionsSaved(committedFields)
        } catch (error: unknown) {
          if (!isContextActive()) {
            return
          }

          markSectionsStatus(savableFieldsAtStart, 'error')
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Autosave failed',
            description: getAutosaveErrorDescription(error),
          })

          if (retryCountRef.current < NOTES_SAVE_MAX_RETRIES) {
            retryCountRef.current += 1
            clearAutosaveTimer()
            autosaveTimeoutRef.current = setTimeout(() => {
              const retrySnapshot = createPendingDraftSaveSnapshot(activeContextRef.current)
              if (retrySnapshot?.context.encounterKey === context.encounterKey) {
                void persistDraftRef.current(retrySnapshot)
              }
            }, NOTES_SAVE_RETRY_DELAY_MS)
          }
        } finally {
          isSettled = true
          inFlightSaveRef.current = null
        }
      })()

      if (!isSettled) {
        inFlightSaveRef.current = saveTask
      }

      return saveTask
    },
    [
      clearAutosaveTimer,
      createPendingDraftSaveSnapshot,
      disableActions,
      markSectionsSaved,
      markSectionsStatus,
      saveSoapNote,
      setNotification,
    ],
  )

  useEffect(() => {
    persistDraftRef.current = persistDraftForContext
  }, [persistDraftForContext])

  // Waits for the running save, then saves everything still dirty in one request.
  const flushAutosaveNow = useCallback(async () => {
    clearAutosaveTimer()

    for (let wait = 0; wait < NOTES_FLUSH_MAX_WAITS && inFlightSaveRef.current; wait += 1) {
      await inFlightSaveRef.current
    }

    const snapshot = createPendingDraftSaveSnapshot(activeContextRef.current)
    if (!snapshot) {
      return
    }

    await persistDraftForContext(snapshot)
  }, [clearAutosaveTimer, createPendingDraftSaveSnapshot, persistDraftForContext])

  useEffect(() => {
    const isEncounterChanged = previousEncounterKeyRef.current !== activeEncounterKey
    previousEncounterKeyRef.current = activeEncounterKey

    clearAutosaveTimer()

    if (!activeEncounterKey) {
      setDraftState(createEmptyDraftState())
      setSectionSaveStatuses(createEmptySectionSaveStatuses())
      setSectionLastSavedAt(createEmptySectionLastSavedAt())
      pendingSelectionRef.current = null
      retryCountRef.current = 0
      initializedEncounterKeyRef.current = null
      return
    }

    if (isSoapContextResolving) {
      setDraftState(createEmptyDraftState())
      if (isEncounterChanged) {
        setSectionSaveStatuses(createEmptySectionSaveStatuses())
        setSectionLastSavedAt(createEmptySectionLastSavedAt())
      }
      initializedEncounterKeyRef.current = null
      return
    }

    const isFirstInitForEncounter = initializedEncounterKeyRef.current !== activeEncounterKey

    setDraftState((prevDraft) => {
      if (isFirstInitForEncounter) {
        return {
          values: { ...seedBySection },
          dirtyFields: createEmptyDirtyFields(),
          lastSavedValues: { ...seedBySection },
        }
      }

      const nextDraft = cloneDraftState(prevDraft)
      sectionKeys.forEach((field) => {
        if (nextDraft.dirtyFields[field]) {
          return
        }

        const nextSeed = seedBySection[field]
        if (nextSeed.trim().length === 0) {
          return
        }

        nextDraft.values[field] = nextSeed
        nextDraft.lastSavedValues[field] = nextSeed
      })
      return nextDraft
    })

    if (isFirstInitForEncounter) {
      initializedEncounterKeyRef.current = activeEncounterKey
      setSectionSaveStatuses(createEmptySectionSaveStatuses())
      setSectionLastSavedAt(createEmptySectionLastSavedAt())
      pendingSelectionRef.current = null
      retryCountRef.current = 0
    }
  }, [
    activeEncounterKey,
    clearAutosaveTimer,
    isSoapContextResolving,
    seedBySection,
    singleNote?.assessment,
    singleNote?.objective,
    singleNote?.subjective,
  ])

  useEffect(() => {
    clearAutosaveTimer()

    if (!canMutate || isSoapContextResolving || !activeEncounterKey) {
      return
    }

    if (getSavableDirtyFields(draftState).length === 0) {
      return
    }

    const scheduledContext = activeContextRef.current
    autosaveTimeoutRef.current = setTimeout(() => {
      if (!scheduledContext || encounterKeyRef.current !== scheduledContext.encounterKey) {
        return
      }

      const scheduledSnapshot = createPendingDraftSaveSnapshot(scheduledContext)
      if (!scheduledSnapshot) {
        return
      }

      void persistDraftRef.current(scheduledSnapshot)
    }, NOTES_AUTOSAVE_DELAY_MS)

    return () => {
      clearAutosaveTimer()
    }
  }, [
    activeEncounterKey,
    canMutate,
    clearAutosaveTimer,
    draftState,
    isSoapContextResolving,
    createPendingDraftSaveSnapshot,
  ])

  useEffect(() => {
    if (!onRegisterLeaveAction) {
      return
    }

    onRegisterLeaveAction(flushAutosaveNow)

    return () => {
      onRegisterLeaveAction(null)
    }
  }, [flushAutosaveNow, onRegisterLeaveAction])

  useEffect(() => {
    return () => {
      clearAutosaveTimer()
    }
  }, [clearAutosaveTimer])

  const toggleSection = useCallback((section: keyof NoteFormValues) => {
    setExpandedSections((prevSections) => {
      const nextSections = new Set(prevSections)

      if (nextSections.has(section)) {
        nextSections.delete(section)
      } else {
        nextSections.add(section)
      }

      return nextSections
    })
  }, [])

  const handleDraftChange = useCallback(
    (field: keyof NoteFormValues, value: string) => {
      if (!canMutate || isSoapContextResolving) {
        return
      }

      retryCountRef.current = 0

      setDraftState((prevDraft) => ({
        values: {
          ...prevDraft.values,
          [field]: value,
        },
        dirtyFields: {
          ...prevDraft.dirtyFields,
          [field]: true,
        },
        lastSavedValues: {
          ...prevDraft.lastSavedValues,
        },
      }))

      setSectionSaveStatuses((prevStatuses) => ({
        ...prevStatuses,
        [field]: sectionLastSavedAt[field] ? 'saved' : 'idle',
      }))
    },
    [canMutate, isSoapContextResolving, sectionLastSavedAt],
  )

  const sectionRefSetters = useMemo<
    Record<keyof NoteFormValues, (instance: TextAreaRef | null) => void>
  >(
    () => ({
      subjective: (instance) => {
        textAreaRefs.current.subjective = instance
      },
      objective: (instance) => {
        textAreaRefs.current.objective = instance
      },
      assessment: (instance) => {
        textAreaRefs.current.assessment = instance
      },
    }),
    [],
  )

  const captureSectionSelection = useCallback((section: keyof NoteFormValues) => {
    const textAreaElement = textAreaRefs.current[section]?.resizableTextArea?.textArea
    pendingSelectionRef.current = textAreaElement
      ? { start: textAreaElement.selectionStart, end: textAreaElement.selectionEnd }
      : null
  }, [])

  const handleInsertSectionLabel = useCallback(
    (section: keyof NoteFormValues, insertValue: string) => {
      if (!canMutate || isSoapContextResolving) {
        return
      }

      const textAreaElement = textAreaRefs.current[section]?.resizableTextArea?.textArea
      const currentValue = draftStateRef.current.values[section]
      const capturedSelection = pendingSelectionRef.current
      const selectionStart =
        capturedSelection?.start ?? textAreaElement?.selectionStart ?? currentValue.length
      const selectionEnd =
        capturedSelection?.end ?? textAreaElement?.selectionEnd ?? currentValue.length
      pendingSelectionRef.current = null

      const { value: nextValue, cursor } = insertTextAtCursor(
        currentValue,
        selectionStart,
        selectionEnd,
        insertValue,
        ' ',
      )

      flushSync(() => {
        handleDraftChange(section, nextValue)
      })

      const restoredElement = textAreaRefs.current[section]?.resizableTextArea?.textArea
      if (restoredElement) {
        restoredElement.focus()
        restoredElement.setSelectionRange(cursor, cursor)
      }
    },
    [canMutate, isSoapContextResolving, handleDraftChange],
  )

  const renderExpandableSections = useCallback(
    (values: NoteFormValues, onChange: (field: keyof NoteFormValues, value: string) => void) => (
      <div className={styles.expandableSections}>
        {sectionKeys.map((section) => {
          const isSectionExpanded = expandedSections.has(section)
          const sectionSaveStatus = sectionSaveStatuses[section]
          const sectionLastSavedTimestamp = sectionLastSavedAt[section]
          const shouldShowLastSavedLabel =
            sectionSaveStatus !== 'saving' &&
            sectionSaveStatus !== 'error' &&
            Boolean(sectionLastSavedTimestamp)
          const sectionStatusLabel = !canMutate
            ? null
            : isSoapContextResolving
            ? null
            : sectionSaveStatus === 'error'
            ? 'Autosave failed'
            : sectionSaveStatus === 'saving'
            ? 'Saving...'
            : shouldShowLastSavedLabel
            ? `Last autosaved at ${sectionLastSavedTimestamp}`
            : 'Input is autosaving'

          return (
            <div key={section} className={styles.sectionBlock}>
              <div
                className={classNames(styles.sectionHeader, {
                  [styles.sectionHeaderCollapsed]: !isSectionExpanded,
                })}
                onClick={() => toggleSection(section)}>
                <span className={styles.sectionTitle}>{sectionLabels[section]}</span>
                <Icon
                  icon={isSectionExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                  width={24}
                  height={24}
                />
              </div>
              {isSectionExpanded && (
                <div className={styles.sectionContent}>
                  {labelsBySection[section].length > 0 && (
                    <div
                      className={styles.labelsBar}
                      role='group'
                      aria-label={`Insert ${sectionLabels[section]} shortcut`}>
                      {labelsBySection[section].map((label, index) => (
                        <Tag
                          key={`${label.key}-${index}`}
                          className={styles.labelChip}
                          role='button'
                          tabIndex={0}
                          aria-label={`Insert ${label.value}`}
                          onPointerDown={(event) => {
                            event.preventDefault()
                            captureSectionSelection(section)
                          }}
                          onClick={() => handleInsertSectionLabel(section, label.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              captureSectionSelection(section)
                              handleInsertSectionLabel(section, label.value)
                            }
                          }}>
                          {label.key.replace(/_/g, ' ')}
                        </Tag>
                      ))}
                    </div>
                  )}
                  <div className={styles.noteFormGroup}>
                    <TextArea
                      ref={sectionRefSetters[section]}
                      autoSize={{ minRows: 3 }}
                      value={values[section]}
                      onChange={(event) => onChange(section, event.target.value)}
                      onBlur={canMutate ? () => void flushAutosaveNow() : undefined}
                      placeholder='Type here...This note will SHOW in medical chart.'
                      readOnly={!canMutate || isSoapContextResolving}
                    />
                  </div>
                  {sectionStatusLabel && (
                    <div className={styles.sectionStatusRow}>
                      <span
                        className={classNames(styles.sectionStatusLabel, {
                          [styles.sectionStatusLabelAutosaving]:
                            sectionSaveStatus === 'idle' && !shouldShowLastSavedLabel,
                          [styles.sectionStatusLabelSaving]: sectionSaveStatus === 'saving',
                          [styles.sectionStatusLabelSaved]: shouldShowLastSavedLabel,
                          [styles.sectionStatusLabelError]: sectionSaveStatus === 'error',
                        })}>
                        {sectionStatusLabel}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    ),
    [
      canMutate,
      captureSectionSelection,
      expandedSections,
      flushAutosaveNow,
      handleInsertSectionLabel,
      isSoapContextResolving,
      labelsBySection,
      sectionRefSetters,
      sectionLastSavedAt,
      sectionSaveStatuses,
      toggleSection,
    ],
  )

  if (isSoapContextResolving) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.loadingWrapper}>
          <Spin size='large' />
        </div>
        <div className={styles.proceduresSection}>
          <ProcedureImageList
            selectedPatientId={selectedPatientId}
            selectedEncounterUid={selectedEncounterUid}
            disableActions={arePhotoActionsDisabled}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.notesList}>
        {renderExpandableSections(draftState.values, handleDraftChange)}
        {telehealthFields.length > 0 && (
          <div className={styles.sectionBlock}>
            <div
              className={classNames(styles.sectionHeader, {
                [styles.sectionHeaderCollapsed]: !isTelehealthExpanded,
              })}
              onClick={() => setIsTelehealthExpanded((prevExpanded) => !prevExpanded)}>
              <span className={styles.sectionTitle}>Telehealth SOAP Notes</span>
              <Icon
                icon={isTelehealthExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                width={24}
                height={24}
              />
            </div>
            {isTelehealthExpanded && (
              <div className={styles.sectionContent}>
                {telehealthFields.map((field) => (
                  <div key={field.key} className={styles.noteSection}>
                    <span className={styles.telehealthFieldLabel}>{field.label}</span>
                    <div className={styles.sectionText}>{field.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.proceduresSection}>
        <ProcedureImageList
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          disableActions={arePhotoActionsDisabled}
        />
      </div>
    </div>
  )
}
